import type { BinaryLockPayload, Hint } from "@monekineko/game-engine";
import {
  $,
  $$,
  Cutscene,
  actorMarkup,
  rand,
  sfx,
  sleep,
  spawnSpark,
  vibrate,
  virusHide,
  virusParts,
  virusReset,
  virusRun,
  virusScare,
  virusTinker,
} from "./core.js";
import type { EngineContext, GameEngine } from "./types.js";

// Уровень «Двоичный замок». Перенос prototypes/1/js/level1.js: та же сцена,
// та же заставка, те же звуки. Отличия: число приходит с сервера, раундов может
// быть несколько, а зачёт делает не клиент.

// Разрядов на табло столько, сколько нужно самому большому числу замка:
// при восьми битах это 255, и в два окошка оно не влезает.
const displayDigits = (bits: number): number => Math.max(2, String((1 << bits) - 1).length);

const panelMarkup = (bits: number, showWeights: boolean): string => {
  const lamps = Array.from({ length: bits }, (_, index) => {
    const bit = bits - 1 - index;
    const weight = 1 << bit;
    return `<button class="lamp" data-bit="${bit}" aria-label="Бит ${weight}"><span>${
      showWeights ? weight : ""
    }</span></button>`;
  }).join("");

  const digits = displayDigits(bits);
  const cells = Array.from(
    { length: digits },
    (_, index) => `<span class="cell"><i class="ghost">8</i><b class="digit" data-digit="${index}"></b></span>`,
  ).join("");

  return `
    <div class="panel">
      <div class="display" style="font-size: ${Math.min(11, 22 / digits)}cqw">${cells}</div>
      <div class="lamps" style="grid-template-columns: repeat(${bits}, ${Math.min(8.4, 38 / bits)}cqw)">${lamps}</div>
    </div>`;
};

const markup = (bits: number, showWeights: boolean, direction: string): string => `
  <div class="screen active" data-level="binary_lock">
    <div class="viewport">
      <div class="camera">
        <div class="stage" data-direction="${direction}">
          <div class="behind"></div>
          <div class="leaf leaf-left">${panelMarkup(bits, showWeights)}</div>
          <div class="leaf leaf-right">${panelMarkup(bits, showWeights)}</div>
          ${actorMarkup("back")}
          <div class="fx"></div>
        </div>
      </div>
    </div>
    <div class="whiteout"></div>
  </div>`;

export function createBinaryLock(root: HTMLElement, ctx: EngineContext): GameEngine {
  const payload = ctx.payload as BinaryLockPayload;
  const bits = payload.bits;
  const digits = displayDigits(bits);
  const binToDec = payload.direction === "bin_to_dec";

  root.innerHTML = markup(bits, payload.showWeights, payload.direction);

  const camera = $(".camera", root)!;
  const stage = $(".stage", root)!;
  const fx = $(".fx", root)!;
  const whiteout = $(".whiteout", root)!;
  const virus = virusParts($(".actor", root)!);

  const state = {
    round: 0,
    value: 0,
    phase: "idle" as "idle" | "intro" | "play" | "solved" | "done",
    values: [] as number[],
  };
  let cut: Cutscene | null = null;
  let alive = true;

  const target = (): number => payload.targets[state.round] ?? 0;

  function setPhase(phase: typeof state.phase): void {
    state.phase = phase;
    stage.dataset.phase = phase;
  }

  function setDigits(text: string): void {
    $$<HTMLElement>(".digit", root).forEach((digit) => {
      digit.textContent = text[Number(digit.dataset.digit)] ?? "";
    });
  }

  function showNumber(value: number): void {
    // незначащие разряды гасим, а не заполняем нулями: так табло читается как число
    setDigits(String(value).padStart(digits, " "));
  }

  function render(): void {
    // в обратном режиме на табло — набранное игроком число, а лампы показывают загаданное
    showNumber(binToDec ? state.value : target());
    const shown = binToDec ? target() : state.value;
    $$<HTMLElement>(".lamp", root).forEach((lamp) => {
      lamp.classList.toggle("on", Boolean(shown & (1 << Number(lamp.dataset.bit))));
    });
  }

  async function nextRound(): Promise<void> {
    state.values.push(state.value);
    ctx.event("round_done", { round: state.round, value: state.value });

    if (state.round + 1 >= payload.targets.length) {
      await solve();
      return;
    }

    sfx.settle();
    state.round++;
    state.value = 0;
    stage.classList.add("flicker");
    await sleep(320);
    if (!alive) return;
    stage.classList.remove("flicker");
    render();
  }

  async function solve(): Promise<void> {
    setPhase("solved");
    const result = await ctx.submit({ values: state.values });
    if (!alive) return;

    if (!result.solved) {
      // сервер не засчитал заход — он закрыт, уровень начинается заново
      sfx.error();
      vibrate([80, 40, 80]);
      stage.animate(
        [{ transform: "translateX(0)" }, { transform: "translateX(-1.5%)" }, { transform: "translateX(1.5%)" }, { transform: "translateX(0)" }],
        { duration: 300, iterations: 2 },
      );
      await sleep(700);
      if (!alive) return;
      setPhase("done");
      ctx.finished(result);
      return;
    }

    stage.classList.add("unlocked");
    sfx.success();
    vibrate([30, 60, 30]);
    await sleep(1000);
    if (!alive) return;

    stage.classList.add("open");
    sfx.door();
    vibrate(120);
    await sleep(1000);
    if (!alive) return;

    sfx.whoosh();
    whiteout.classList.add("show");
    await sleep(900);
    if (!alive) return;

    setPhase("done");
    ctx.finished(result);
  }

  function onLamp(lamp: HTMLElement): void {
    if (state.phase !== "play" || binToDec) return;
    const bit = Number(lamp.dataset.bit);
    state.value ^= 1 << bit;
    const isOn = Boolean(state.value & (1 << bit));
    isOn ? sfx.lampOn() : sfx.lampOff();
    vibrate(12);
    ctx.event("bit_toggled", { bit, on: isOn });
    render();
    if (state.value === target()) void nextRound();
  }

  function onStep(delta: number): void {
    if (state.phase !== "play" || !binToDec) return;
    const max = (1 << bits) - 1;
    state.value = Math.max(0, Math.min(max, state.value + delta));
    sfx.key(delta > 0 ? 1 : 0);
    vibrate(8);
    render();
    if (state.value === target()) void nextRound();
  }

  stage.addEventListener("click", (event) => {
    const lamp = (event.target as HTMLElement).closest<HTMLElement>(".lamp");
    if (lamp) onLamp(lamp);
    const step = (event.target as HTMLElement).closest<HTMLElement>("[data-step]");
    if (step) onStep(Number(step.dataset.step));
  });

  // В обратном режиме лампы не нажимаются, зато под табло появляются стрелки
  if (binToDec) {
    $$<HTMLElement>(".panel", root).forEach((panel) => {
      const stepper = document.createElement("div");
      stepper.className = "stepper";
      stepper.innerHTML = `
        <button data-step="-1" aria-label="Меньше">−</button>
        <button data-step="1" aria-label="Больше">+</button>`;
      panel.appendChild(stepper);
    });
  }

  // ---------- Заставка ----------

  function glitchDigits(): void {
    const chars = "0123456789ABCDEF#%?";
    const pick = (): string => chars[Math.floor(Math.random() * chars.length)]!;
    setDigits(Array.from({ length: digits }, pick).join(""));
  }

  async function playIntro(): Promise<void> {
    cut = new Cutscene();
    const c = cut;
    setPhase("intro");

    camera.style.transform = "scale(1.3)";
    virusReset(virus, "back");
    stage.classList.add("glitch");
    glitchDigits();

    const tinker = virusTinker(c, virus);
    const sparks = c.every(110, () => {
      spawnSpark(fx, rand(52, 64), rand(42, 52));
      if (Math.random() < 0.6) sfx.tinker();
    });
    const glitch = c.every(90, glitchDigits);

    try {
      await c.later(2600);
      clearInterval(sparks);
      await virusScare(c, virus, tinker);

      virusRun(c, virus);
      c.track(
        camera.animate([{ transform: "scale(1.3)" }, { transform: "scale(1)" }], {
          duration: 1500,
          easing: "cubic-bezier(.55,0,.25,1)",
          fill: "forwards",
        }),
      );

      await c.later(450);
      clearInterval(glitch);
      stage.classList.remove("glitch");
      render();
      stage.classList.add("flicker");
      sfx.settle();

      await c.later(1100);
    } catch {
      return;
    }
    finishIntro();
  }

  function stopIntro(): void {
    if (!cut) return;
    cut.stop();
    cut = null;
    fx.replaceChildren();
    virusHide(virus);
    camera.style.transform = "";
    stage.classList.remove("glitch", "flicker");
  }

  function finishIntro(): void {
    stopIntro();
    render();
    setPhase("play");
    ctx.introDone();
  }

  if (ctx.playIntro) void playIntro();
  else finishIntro();

  return {
    destroy() {
      alive = false;
      stopIntro();
      root.innerHTML = "";
    },
    skipIntro: finishIntro,
    canHint: () => state.phase === "play",
    applyHint(hint: Hint) {
      const data = hint.data as { bit: number; on: boolean };
      const lamp = $<HTMLElement>(`.leaf-left .lamp[data-bit="${data.bit}"]`, root);
      const twin = $<HTMLElement>(`.leaf-right .lamp[data-bit="${data.bit}"]`, root);
      [lamp, twin].forEach((element) => {
        if (!element) return;
        element.animate(
          [
            { boxShadow: "0 0 0 0.5cqw #2a3440" },
            { boxShadow: "0 0 3cqw 1cqw rgba(62,240,255,0.9)" },
            { boxShadow: "0 0 0 0.5cqw #2a3440" },
          ],
          { duration: 700, iterations: 3 },
        );
      });
      sfx.lcd();
    },
  };
}
